import React from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { paths } from "ui/routes";
import { cx } from "utils/join-classnames";
import { useFormValidation } from "ui/hooks/use-form-validation";
import { useSessionContext } from "ui/context/SessionContext";
import { Button, Spinner, FormElement } from "ui/components";
import { authenticateUser } from "core/services/authserver";
import { requestPasswordReset } from "core/services/site/user";
import styles from "./Login.module.scss";

type ViewMode = "login" | "forgot" | "forgot.success";

const Login: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const sessionContext = useSessionContext();

    const checkOrigin = () => {
        const validPaths = Object.keys(paths).map((key: string) => paths[key]);
        const origin = new URLSearchParams(location.search).get("origin");
        if (!origin || !validPaths.some((path) => origin.startsWith(path))) {
            return undefined;
        }
        return origin;
    };

    const [origin] = React.useState<string>(() => checkOrigin() || "");
    const theForm = useFormValidation({
        email: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: true,
                minLength: 2,
                maxLength: 200,
            },
        },
        password: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: true,
                minLength: 6,
                maxLength: 50,
            },
        },
    });

    React.useEffect(() => {
        if (sessionContext.session) {
            navigate(paths.projectsPage);
        }
    }, [navigate, sessionContext.session]);

    const [pending, setPending] = React.useState<boolean>(false);
    const [mode, setMode] = React.useState<ViewMode>("login");
    const [loginError, setLoginError] = React.useState<string | null>(null);

    // Reset password form validation
    const pwdForm = useFormValidation({
        email: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: true,
                isEmail: true,
            },
        },
    });

    // Action handling
    const handleForgotPassword = async (ev: React.FormEvent): Promise<void> => {
        ev.preventDefault();
        try {
            if (!pwdForm.formState.email.value) {
                throw Error("no email found");
            }
            setPending(true);
            await requestPasswordReset(
                pwdForm.formState.email.value.toString(),
            );
            setPending(false);
            setMode("forgot.success");
        } catch (err) {
            console.error("error during password reset", err);
        }
    };

    const handleLogin = async (ev: React.FormEvent): Promise<void> => {
        ev.preventDefault();
        try {
            if (!theForm.formState.email.value) {
                throw Error("no email found");
            }
            if (!theForm.formState.password.value) {
                throw Error("no password found");
            }
            setLoginError(null);
            setPending(true);
            const response = await authenticateUser(
                theForm.formState.email.value.toString(),
                theForm.formState.password.value.toString(),
            );
            if (response.error || !response.value) {
                setLoginError(
                    "Invalid email or password.\nIf you've used SpotOn before you might have to reset your password first.",
                );
                setPending(false);
                return;
            }
            setPending(false);
            sessionContext.setVerified(response.value.user.verified);
            sessionContext.setSession(response.value);
            if (origin) {
                navigate(origin);
            } else {
                navigate(paths.projectsPage);
            }
        } catch (err) {
            setLoginError(`Something went wrong: ${err}`);
            setPending(false);
        }
    };

    React.useEffect(() => {
        setLoginError(null);
    }, [mode]);

    const currentYear = new Date().getFullYear();

    return (
        <div className={styles.page}>
            <div className={styles.login}>
                <img
                    src="./static/img/logo-spoton.svg"
                    alt="SpotOn"
                    className={styles.login__logo}
                />
                <div className={styles.login__heading}>
                    {mode === "login" && "Login"}
                    {mode === "forgot" && "Forgot password?"}
                    {mode === "forgot.success" && "Request has been sent"}
                </div>
                <div
                    className={cx(
                        styles.login__form,
                        loginError && styles.login__form__error,
                    )}
                >
                    {loginError && (
                        <div className={styles.login__error}>{loginError}</div>
                    )}
                    {pending && (
                        <span className={styles.login__spinner}>
                            <Spinner />
                        </span>
                    )}
                    {mode === "forgot.success" && (
                        <>
                            <div className={styles.login__text}>
                                If your account is known to us, we have sent you
                                a link to reset your password. It can take a few
                                minutes.
                            </div>
                            <br />
                            <Button
                                type="primary"
                                className={styles.backToLoginBtn}
                                onClick={() => setMode("login")}
                            >
                                Back to login
                            </Button>
                        </>
                    )}
                    {mode === "forgot" && (
                        <>
                            <div>
                                {!pending && (
                                    <div className={styles.login__text}>
                                        Enter your email to request a password
                                        reset
                                    </div>
                                )}
                                <FormElement
                                    title="Email"
                                    onChange={(value) => {
                                        pwdForm.inputChangeHandler(
                                            "email",
                                            value,
                                        );
                                    }}
                                    defaultText="your@email.com"
                                    theme="dark"
                                    type="text"
                                    id="email"
                                    fieldState={pwdForm.formState.email}
                                />
                                <Button
                                    type="primary"
                                    onClick={handleForgotPassword}
                                    disabled={!pwdForm.formValid || pending}
                                    className={cx(
                                        styles.login__btn,
                                        styles.login__btn__primary,
                                    )}
                                >
                                    Request reset
                                </Button>
                            </div>
                            <br />
                            <a
                                onClick={() => setMode("login")}
                                className={styles.login__link}
                                role="presentation"
                            >
                                Back to login
                            </a>
                        </>
                    )}
                    {mode === "login" && (
                        <>
                            <form onSubmit={handleLogin}>
                                <FormElement
                                    title="Email"
                                    onChange={(value) => {
                                        theForm.inputChangeHandler(
                                            "email",
                                            value,
                                        );
                                    }}
                                    defaultText="Email"
                                    theme="dark"
                                    type="text"
                                    id="email"
                                    fieldState={theForm.formState.email}
                                />
                                <FormElement
                                    title="Password"
                                    onChange={(value) => {
                                        theForm.inputChangeHandler(
                                            "password",
                                            value,
                                        );
                                    }}
                                    defaultText="Password"
                                    theme="dark"
                                    type="password"
                                    id="password"
                                    fieldState={theForm.formState.password}
                                />
                                <Button
                                    type="primary"
                                    action="submit"
                                    disabled={!theForm.formValid || pending}
                                    value="Log in"
                                    className={cx(
                                        styles.login__btn,
                                        styles.login__btn__primary,
                                    )}
                                >
                                    <span
                                        className={styles.login__button__label}
                                    >
                                        {pending ? "Logging in…" : "Log in"}
                                    </span>
                                </Button>
                            </form>
                            <a
                                onClick={() => setMode("forgot")}
                                className={styles.login__link}
                                role="presentation"
                            >
                                Forgot Password?
                            </a>
                        </>
                    )}
                </div>
                <div className={styles.login__footer}>
                    <p>
                        &copy; {currentYear} SpotOn, part of{" "}
                        <a
                            href="https://www.wearetriple.com"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Triple
                        </a>
                        . All right reserved.
                    </p>
                    <p>
                        <a
                            href="https://spot-on.media/privacy-policy/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Privacy Policy
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
